import React, {useMemo, useState} from 'react';
import {
  IFdApplicationType,
  IFdFinancialPerformance,
  ISupplierUserProfile,
} from 'constants/interfaces';
import {
  reduxToggleSidePanel,
  reduxSetSidePanelEditState,
} from 'appredux/actions';
import {
  FdFinancialHistoryForm,
  FinancialSubmission,
} from 'components/applications/FdFinancialHistoryForm';
import {apiUpdateFdFinancialPerformance} from 'constants/Api';
import {Route} from 'react-router-dom';
import CheckIcon from 'components/CheckIcon/CheckIcon';
import {Button} from '@material-ui/core';

const AddOrUpdateFdFinancialHistory = (props: {
  user: ISupplierUserProfile;
  allHistory: IFdFinancialPerformance[];
  applicationId: number;
  companyId: number;
  application: IFdApplicationType;
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [hasSuccessfullyUpdated, setHasSuccessfullyUpdated] = useState(false);

  /* cancel */
  const handleCancel = () => {
    reduxToggleSidePanel(false, null);
  };

  /* submit */
  const handleSubmit = (submission: Partial<FinancialSubmission>[]) => {
    setIsLoading(true);

    Promise.all(
      submission.map((item) => {
        const existing = props.allHistory.find((history) => {
          return history.year === item.year;
        });

        return apiUpdateFdFinancialPerformance({
          year: item.year!,
          revenue: item.revenue!,
          expenses: item.expenses!,
          operatingCosts: item.operatingCosts,
          profit: item.profit!,
          financialYearEnd:
            item.financialYearEnd ?? existing?.financialYearEnd ?? undefined,
          financialYearEndMonth:
            item.financialYearEndMonth ??
            existing?.financialYearEndMonth ??
            undefined,
          notes: item.notes ?? '', // NEW
          companyId: props.companyId,
          applicationId: props.applicationId,
        });
      }),
    )
      .then(() => {
        setHasSuccessfullyUpdated(true);
        reduxSetSidePanelEditState('saved');
      })
      .catch((err) => {
        alert(
          'Failed to update financial history at the moment. Please try again later',
        );
        console.error('Failed to update financial history', err);
      })
      .finally(() => setIsLoading(false));
  };

  const canWrite = true;

  /* build default rows */
  const formData = useMemo(() => {
    const rows: Partial<FinancialSubmission>[] = [];
    const years = [2024, 2023, 2022];
    for (const yr of years) {
      const found = props.allHistory.find((h) => h.year === yr);
      rows.push(
        found
          ? {
              id: found.id,
              year: found.year,
              revenue: found.revenue,
              expenses: found.expenses,
              operatingCosts: found.operatingCosts,
              profit: found.profit,
              financialYearEnd: found.financialYearEnd ?? undefined,
              financialYearEndMonth: found.financialYearEndMonth ?? undefined,
              notes: found.notes ?? '', // NEW
            }
          : {year: yr},
      );
    }
    return rows;
  }, [props.allHistory]);

  /* success screen */
  if (hasSuccessfullyUpdated) {
    return (
      <Route
        render={() => (
          <div className="side-panel-success-container">
            <div className="mid-section">
              <div className="success-gif">
                <CheckIcon />
              </div>
              <div className="success-msg">
                Financial history updated successfully
              </div>
            </div>
            <div className="btn-container">
              <Button
                className="primary-btn"
                variant="outlined"
                color="primary"
                onClick={() => reduxToggleSidePanel(false, null)}
              >
                Done
              </Button>
            </div>
          </div>
        )}
      />
    );
  }

  /* render form */
  return (
    <FdFinancialHistoryForm
      defaultValues={formData}
      isLoading={isLoading}
      submitButtonTitle="Update"
      onSubmit={handleSubmit}
      cancelButtonTitle="Cancel"
      onCancel={handleCancel}
      readonly={!canWrite}
    />
  );
};

export default AddOrUpdateFdFinancialHistory;
